import type { MapLocation } from './types';

/** Lowercase, strip accents and punctuation, and spell out "&" so "Wilson & Co" ~ "wilson and co". */
export function normalizeText(text: string): string {
  return text
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/['’.]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function scoreTitle(title: string, query: string, tokens: string[]): number {
  if (title === query) return 100;
  if (title.startsWith(query)) return 80;
  const words = title.split(' ');
  if (words.some((w) => w.startsWith(query))) return 60;
  // Every query word starts some title word: "rush lib" -> "Rush Rhees Library".
  if (tokens.length > 1 && tokens.every((t) => words.some((w) => w.startsWith(t)))) return 50;
  if (title.includes(query)) return 40;
  return 0;
}

export interface SearchIndexEntry {
  location: MapLocation;
  title: string;
  /** Normalized aliases from Directus (nicknames, abbreviations, old names). */
  aliases: string[];
}

export function buildSearchIndex(locations: MapLocation[]): SearchIndexEntry[] {
  return locations.map((location) => ({
    location,
    title: normalizeText(location.title),
    aliases: location.aliases.map(normalizeText).filter(Boolean),
  }));
}

/** Title match, or an alias match ranked just below the same kind of title match. */
function scoreEntry(entry: SearchIndexEntry, query: string, tokens: string[]): number {
  let best = scoreTitle(entry.title, query, tokens);
  for (const alias of entry.aliases) best = Math.max(best, scoreTitle(alias, query, tokens) - 5);
  return best;
}

export function searchLocations(index: SearchIndexEntry[], rawQuery: string, limit = 8): MapLocation[] {
  const query = normalizeText(rawQuery);
  if (!query) return [];
  const tokens = query.split(' ');
  return index
    .map((entry) => ({ entry, score: scoreEntry(entry, query, tokens) }))
    .filter((r) => r.score > 0)
    // Higher score first, then shorter titles (closer match), then alphabetical.
    .sort(
      (a, b) =>
        b.score - a.score ||
        a.entry.title.length - b.entry.title.length ||
        a.entry.title.localeCompare(b.entry.title),
    )
    .slice(0, limit)
    .map((r) => r.entry.location);
}
